import React, { useEffect, useRef, useCallback } from 'react'
import { Route, Switch, Redirect, useHistory, useLocation } from 'react-router-dom'
import { observer } from 'mobx-react'
import { observe } from 'mobx'
import { Layout } from 'antd'
import { useReducedMotion } from '@pushly/aqe/lib/hooks/use-reduced-motion'
import { SessionState } from '@pushly/aqe'
import { useAppState, useAppService, useMobileService } from '../hooks/use-mobile-service'
import { AbilityAction } from '../../enums/ability-action.enum'
import { SubjectEntity } from '../../enums/ability-entity.enum'
import { MobileHome } from '../home/mobile-home'
import { MobileCompose } from '../notifications/mobile-compose'
import { NotificationDetail } from '../notification-detail/notification-detail'
import { InsightsView } from '../insights/insights-view'
import { MobileHeader } from '../header/mobile-header'
import { BottomTabBar } from '../tab-bar/bottom-tab-bar'
import { MobileErrorBoundary } from '../components/mobile-error-boundary'
import { ProtectedRoute } from '../components/protected-route'
import { recoverPushSubscriptionIfNeeded } from '../utils/push-subscription-recovery'
import { SessionExpiryAdapter } from '../../components/session-expiry-adapter/session-expiry-adapter'
import { SessionExpirationScreen } from '../../components/session-expiration-screen/session-expiration-screen'
import PushlyIcon from '@pushly/aqe/lib/components/icons/pushly-icon'
import './mobile-root.scss'

const HEADER_HEIGHT = 56
const TAB_BAR_HEIGHT = 56

export const MobileRoot: React.FC = observer(() => {
    const appService = useAppService()
    const appState = useAppState()
    const sessionState = useMobileService(SessionState)
    const contentRef = useRef<HTMLElement>(null)
    const history = useHistory()
    const location = useLocation()
    const prefersReducedMotion = useReducedMotion()

    // Attach history container for AppService routing
    useEffect(() => {
        appService.attachHistoryContainer(history)
    }, [history, appService])

    // Recover lapsed push subscription on iOS PWA (Safari 7-day expiry)
    useEffect(() => {
        recoverPushSubscriptionIfNeeded()
    }, [])

    // When domain changes, redirect to dashboard if on a domain-specific sub-page
    // (notification detail, compose, edit) to avoid loading stale IDs from the old domain
    useEffect(() => {
        const dispose = observe(appState, 'currentDomainJsonData', () => {
            const domainId = appState.currentDomain?.id
            if (!domainId) return

            const path = location.pathname
            const isDashboard = /\/domains\/\d+\/dashboard/.test(path)
            const isInsights = /\/domains\/\d+\/insights/.test(path)

            // Only redirect if on a sub-page (detail, compose, edit) — not dashboard or insights
            if (!isDashboard && !isInsights) {
                history.replace(`/domains/${domainId}/dashboard`)
            }
        })

        return () => dispose()
    }, [appState, history, location.pathname])

    // Set CSS custom properties on mount
    useEffect(() => {
        const root = document.documentElement

        root.style.setProperty('--mobile-header-height', `${HEADER_HEIGHT}px`)
        root.style.setProperty('--mobile-tab-bar-height', `${TAB_BAR_HEIGHT}px`)
        root.style.setProperty('--mobile-safe-area-top', 'env(safe-area-inset-top, 0px)')
        root.style.setProperty('--mobile-safe-area-bottom', 'env(safe-area-inset-bottom, 0px)')

        // Prevent iOS Safari from zooming on input focus
        const viewport = document.querySelector('meta[name="viewport"]')
        const originalContent = viewport?.getAttribute('content') ?? ''
        if (viewport) {
            viewport.setAttribute('content', `${originalContent}, maximum-scale=1, user-scalable=no`)
        }

        return () => {
            root.style.removeProperty('--mobile-header-height')
            root.style.removeProperty('--mobile-tab-bar-height')
            root.style.removeProperty('--mobile-safe-area-top')
            root.style.removeProperty('--mobile-safe-area-bottom')

            // Restore original viewport on unmount (switching to desktop)
            if (viewport && originalContent) {
                viewport.setAttribute('content', originalContent)
            }
        }
    }, [])

    // Viewport height calculation with dvh fallback
    useEffect(() => {
        const root = document.documentElement

        // Test if dvh is supported
        const testEl = document.createElement('div')
        testEl.style.height = '100dvh'
        const supportsDvh = testEl.style.height === '100dvh'

        if (!supportsDvh) {
            const updateVh = () => {
                const vh = window.innerHeight
                root.style.setProperty('--mobile-vh', `${vh}px`)
            }

            updateVh()
            window.addEventListener('resize', updateVh)

            return () => {
                window.removeEventListener('resize', updateVh)
                root.style.removeProperty('--mobile-vh')
            }
        }

        return undefined
    }, [])

    // Wire up content container ref for AppService
    const setContentRef = useCallback(
        (el: HTMLElement | null) => {
            ;(contentRef as React.MutableRefObject<HTMLElement | null>).current = el
            appService.contentContainerRef = el
        },
        [appService],
    )

    // Guard: if domain hasn't loaded yet, show a loading state
    // This prevents null-reference errors in child components that assume currentDomain exists
    if (!appState.currentDomain) {
        return (
            <Layout className="mobile-root">
                <Layout.Content
                    className="mobile-root__content"
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                >
                    <span className="mobile-root__loading-icon" aria-label="Loading">
                        <PushlyIcon aria-hidden="true" />
                    </span>
                </Layout.Content>
            </Layout>
        )
    }

    return (
        <Layout className="mobile-root" data-reduced-motion={prefersReducedMotion ? 'true' : undefined}>
            <SessionExpiryAdapter />
            <SessionExpirationScreen />

            <header className="mobile-root__header">
                <MobileHeader />
            </header>

            <Layout.Content className="mobile-root__content" ref={setContentRef} role="main">
                {appState.isDomainSwitching && (
                    <div className="mobile-root__domain-switch-overlay" aria-label="Switching domain">
                        <span className="mobile-root__loading-icon">
                            <PushlyIcon aria-hidden="true" />
                        </span>
                    </div>
                )}
                <MobileErrorBoundary>
                    <Switch>
                        <Route path="/domains/:domainId([0-9]+)/dashboard" component={MobileHome} />
                        <ProtectedRoute
                            path="/domains/:domainId([0-9]+)/notifications/new"
                            render={() => <MobileCompose formMode="create" />}
                            action={AbilityAction.CREATE}
                            subject={SubjectEntity.NOTIFICATION}
                        />
                        <ProtectedRoute
                            path="/domains/:domainId([0-9]+)/notifications/edit/:notifId([0-9]+)"
                            render={() => <MobileCompose formMode="edit" />}
                            action={AbilityAction.UPDATE}
                            subject={SubjectEntity.NOTIFICATION}
                        />
                        <Route
                            path="/domains/:domainId([0-9]+)/notifications/:notifId([0-9]+)/summary"
                            component={NotificationDetail}
                        />
                        <Route path="/domains/:domainId([0-9]+)/insights" component={InsightsView} />
                        {sessionState.isAuthenticated && appState.currentDomain && (
                            <Redirect to={`/domains/${appState.currentDomain.id}/dashboard`} />
                        )}
                    </Switch>
                </MobileErrorBoundary>
            </Layout.Content>

            <nav className="mobile-root__tab-bar">
                <BottomTabBar />
            </nav>
        </Layout>
    )
})

MobileRoot.displayName = 'MobileRoot'
